import React from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'

import { WalletAdjustmentTableDataType } from './SearchWallet'

// Column helper for type safety
const columnHelper = createColumnHelper<WalletAdjustmentTableDataType>()

// Define columns
const columns = [
  columnHelper.accessor('company_name', {
    header: 'Company Name',
    cell: (info) => (
      <Typography variant='body2' fontWeight='medium'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('reference_number', {
    header: 'Reference Number',
    cell: (info) => (
      <Typography variant='body2' fontFamily='monospace'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('note', {
    header: 'Note',
    cell: (info) => (
      <Typography variant='body2' fontWeight='600'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('adjustment_type', {
    header: 'Adjustment Type',
    cell: (info) => (
      <Typography variant='body2' fontWeight='600'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('amount', {
    header: 'Amount',
    cell: (info) => <Typography variant='body2'>{info.getValue()}</Typography>
  }),
  columnHelper.accessor('created_by', {
    header: 'Created By',
    cell: (info) => (
      <Typography variant='body2' color='text.secondary'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('created_on', {
    header: 'Created On',
    cell: (info) => (
      <Typography variant='body2' color='text.secondary'>
        {new Date(info.getValue()).toLocaleDateString()}
      </Typography>
    )
  }),
  columnHelper.accessor('updated_by', {
    header: 'Updated By',
    cell: (info) => (
      <Typography variant='body2' color='text.secondary'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('updated_on', {
    header: 'Updated On',
    cell: (info) => (
      <Typography variant='body2' color='text.secondary'>
        {new Date(info.getValue()).toLocaleDateString()}
      </Typography>
    )
  }),
  columnHelper.accessor('transaction_date_time', {
    header: 'Transaction Date & Time',
    cell: (info) => (
      <Typography variant='body2'>
        {new Date(info.getValue()).toLocaleString()}
      </Typography>
    )
  }),
  columnHelper.accessor('updated_on', {
    header: 'Updated On',
    cell: (info) => (
      <Typography variant='body2' color='text.secondary'>
        {new Date(info.getValue()).toLocaleDateString()}
      </Typography>
    )
  })
]

export default function WalletAdjustmentList({
  data
}: {
  data: WalletAdjustmentTableDataType[]
}) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel()
  })

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'grey.50',
                      py: 2
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  '&:nth-of-type(odd)': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} sx={{ py: 2.5 }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Empty state */}
        {table.getRowModel().rows.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color='text.secondary'>No record found</Typography>
          </Box>
        )}
      </TableContainer>
    </Box>
  )
}
